import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip,
  InputAdornment,
  Tooltip,
  useTheme,
  Alert,
  CircularProgress,
  Snackbar,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";

import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import { authGet, authPost, authPostForm, authPatch, authFetchBlob, unwrapRows } from "../../lib/authApi";

const dt = (v) => (v ? new Date(v).toLocaleString() : "—");

const colorForStatus = (s) => {
  const k = String(s || "").toLowerCase();
  if (k === "sent" || k === "completed" || k === "final") return "success";
  if (k === "processing") return "info";
  if (k === "review" || k === "draft") return "warning";
  if (k === "error" || k === "failed") return "error";
  return "default";
};

// ✅ Robust row extraction for your various backend shapes
function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.data)) return resp.data;
  return unwrapRows(resp) || [];
}

function ImportToxCsvDialog({ open, onClose, orgId, testType, onImported }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState([]);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setBusy(false);
    setErr("");
    setPreview([]);
  }, [open]);

  const pick = (f) => {
    setErr("");
    setPreview([]);
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      setErr("Please select a .csv, .xlsx, or .xls result file.");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pick(e.dataTransfer?.files?.[0]);
  };

  const formData = () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("org_id", orgId);
    fd.append("test_type", testType);
    return fd;
  };

  const previewNow = async () => {
    if (!orgId) return setErr("Select an organization first.");
    if (!file) return setErr("Pick a CSV/Excel file first.");
    if (testType !== "TOX") return setErr("Only TOX import is enabled right now.");
    setBusy(true);
    setErr("");
    try {
      const resp = await authPostForm("/api/tox/import/preview", formData());
      setPreview(resp?.samples || []);
    } catch (e) {
      setErr(e.message || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const commitMatched = async () => {
    if (!preview.some((s) => s.status === "matched")) return setErr("No matched intake orders found. Create Intake orders first or use legacy import intentionally.");
    setBusy(true);
    setErr("");
    try {
      await authPostForm("/api/tox/import/commit", formData());
      onImported?.();
      onClose();
    } catch (e) {
      setErr(e.message || "Import failed.");
      setBusy(false);
    }
  };

  const legacyImport = async () => {
    if (!window.confirm("Legacy import creates new TOX panels/accessions from the file instead of matching Intake orders. Continue?")) return;
    setBusy(true);
    setErr("");
    try {
      await authPostForm("/api/tox/import", formData());
      onImported?.();
      onClose();
    } catch (e) {
      setErr(e.message || "Legacy import failed.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Upload {testType} Results</DialogTitle>

      <DialogContent dividers>
        {!orgId ? <Alert severity="warning" sx={{ mb: 2 }}>Pick an organization first.</Alert> : null}
        <Alert severity="info" sx={{ mb: 2 }}>
          Preferred workflow: create the order in Intake first, then upload the instrument CSV/Excel here. Samples are matched by accession number.
        </Alert>

        <Box
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          sx={{ p: 2, border: "2px dashed", borderColor: file ? "success.main" : "divider", borderRadius: 2, bgcolor: "background.default" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <UploadFileIcon />
            <Box flex={1}>
              <Typography variant="subtitle1">{file ? file.name : "Drop CSV/Excel here or choose file"}</Typography>
              <Typography variant="body2" color="text.secondary">
                The file should include Accession Number, Accession, Sample ID, or Sample Name matching an Intake order accession.
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()} disabled={busy}>Choose</Button>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,text/csv" style={{ display: "none" }} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          </Box>
        </Box>

        {preview.length ? (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>Match Preview</Typography>
            <Box sx={{ maxHeight: 320, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              {preview.map((s) => (
                <Box key={s.accession_number} display="grid" gridTemplateColumns="1.2fr .7fr 2fr .8fr" gap={1} p={1} borderBottom="1px solid" borderColor="divider">
                  <Typography variant="body2">{s.accession_number}</Typography>
                  <Typography variant="body2">{s.result_count} results</Typography>
                  <Typography variant="body2">{s.matched_order ? `${s.matched_order.patient || "—"} • ${s.matched_order.status || "—"}` : "No Intake order match"}</Typography>
                  <Chip size="small" color={s.status === "matched" ? "success" : "warning"} label={s.status} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {err ? <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert> : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={legacyImport} disabled={busy || !file || !orgId} color="warning">Legacy Import</Button>
        <Button variant="outlined" onClick={previewNow} disabled={busy || !file || !orgId} startIcon={busy ? <CircularProgress size={16} /> : <UploadFileIcon />}>Preview Matches</Button>
        <Button variant="contained" onClick={commitMatched} disabled={busy || !file || !orgId || !preview.some((s) => s.status === "matched")}>Commit Matched Results</Button>
      </DialogActions>
    </Dialog>
  );
}

function PanelHubDialog({ open, onClose, orgId, panelId, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [bundle, setBundle] = useState(null);

  const [providers, setProviders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [patients, setPatients] = useState([]);

  const [addProvOpen, setAddProvOpen] = useState(false);
  const [addFacOpen, setAddFacOpen] = useState(false);
  const [addPatOpen, setAddPatOpen] = useState(false);

  const [newProv, setNewProv] = useState({ first_name: "", last_name: "", npi: "" });

  // ✅ ADD CLIA + LD here
  const [newFac, setNewFac] = useState({ name: "", address1: "", city: "", state: "", zip: "", phone: "", clia: "", ld: "" });

  const [newPat, setNewPat] = useState({
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    patient_external_id: "",
  });

  const patientLabel = (x) =>
    x?.full_name || [x?.first_name, x?.last_name].filter(Boolean).join(" ") || x?.patient_name || "—";

  const toDateInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const toDateTimeLocalInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    // convert to local "YYYY-MM-DDTHH:mm"
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const calcAge = (dob) => {
    const d = dob ? new Date(dob) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age >= 0 ? String(age) : "";
  };

  const providerById = useMemo(() => new Map(providers.map((p) => [p.id, p])), [providers]);
  const facilityById = useMemo(() => new Map(facilities.map((f) => [f.id, f])), [facilities]);
  const patientById = useMemo(() => new Map(patients.map((p) => [p.id, p])), [patients]);

  const [form, setForm] = useState({
    patient_id: "",
    provider_id: "",
    facility_id: "",

    patient_external_id: "",
    patient_dob: "",
    patient_gender: "",

    specimen_source: "",
    sample_type: "",
    medications: "",

    report_date: "",
    collected_at: "",
    received_at: "",
    requisition_at: "",
  });

  const load = async () => {
    if (!open || !orgId || !panelId) return;
    setBusy(true);
    setErr("");
    try {
      const [details, prov, fac, pat] = await Promise.all([
        authGet(`/api/tox/panels/${encodeURIComponent(panelId)}/details?org_id=${encodeURIComponent(orgId)}`),
        authGet(`/api/providers?org_id=${encodeURIComponent(orgId)}&limit=2000`),
        authGet(`/api/facilities?org_id=${encodeURIComponent(orgId)}&limit=2000`),
        authGet(`/api/patients?org_id=${encodeURIComponent(orgId)}&limit=2000`),
      ]);

      setBundle(details);

      const provRows = prov?.providers || pickRows(prov);
      const facRows = fac?.facilities || pickRows(fac);
      const patRows = pat?.patients || pickRows(pat);

      setProviders(provRows);
      setFacilities(facRows);
      setPatients(patRows);

      const panel = details?.panel || {};
      const linkedPatient = panel.patient_id ? patRows.find((x) => x.id === panel.patient_id) : null;

      setForm({
        patient_id: panel.patient_id || "",
        provider_id: panel.provider_id || "",
        facility_id: panel.facility_id || "",

        patient_external_id:
          (linkedPatient?.patient_external_id ??
            linkedPatient?.external_id ??
            panel.patient_external_id ??
            "") || "",
        patient_dob: toDateInput(
          linkedPatient?.dob ??
            linkedPatient?.date_of_birth ??
            panel.patient_dob ??
            panel.patient_dob_actual ??
            panel.patient_dob_final
        ),
        patient_gender: (linkedPatient?.gender ?? panel.patient_gender ?? panel.patient_gender_final ?? "") || "",

        specimen_source: panel.specimen_source || "",
        sample_type: panel.sample_type || "",
        medications: panel.medications || "",

        report_date: toDateTimeLocalInput(
          panel.report_date || panel.imported_at || panel.created_at
        ),
        collected_at: toDateInput(panel.collected_at),
        received_at: toDateInput(panel.received_at),
        requisition_at: toDateInput(panel.requisition_at),
      });
    } catch (e) {
      setErr(e.message || "Failed to load details.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgId, panelId]);

  const saveHeader = async () => {
    if (!orgId || !panelId) return;
    setBusy(true);
    setErr("");
    try {
      await authPatch(`/api/tox/panels/${encodeURIComponent(panelId)}`, {
        org_id: orgId,
        patient_id: form.patient_id || null,
        provider_id: form.provider_id || null,
        facility_id: form.facility_id || null,

        patient_external_id: form.patient_external_id || null,
        patient_dob: form.patient_dob || null,
        patient_gender: form.patient_gender || null,

        specimen_source: form.specimen_source || null,
        sample_type: form.sample_type || null,
        medications: form.medications || null,

        report_date: form.report_date || null,
        collected_at: form.collected_at || null,
        received_at: form.received_at || null,
        requisition_at: form.requisition_at || null,
      });

      if (form.patient_id) {
        await authPatch(`/api/patients/${encodeURIComponent(form.patient_id)}`, {
          org_id: orgId,
          patient_external_id: form.patient_external_id || null,
          dob: form.patient_dob || null,
          gender: form.patient_gender || null,
        });
      }

      onSaved?.();
      await load();
    } catch (e) {
      setErr(e.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const createProvider = async () => {
    if (!orgId) return;
    setBusy(true);
    setErr("");
    try {
      const payload = {
        org_id: orgId,
        first_name: newProv.first_name?.trim() || null,
        last_name: newProv.last_name?.trim() || null,
        npi: newProv.npi?.trim() || null,
      };

      const resp = await authPost("/api/providers", payload);
      const created = resp?.provider || resp?.row || (Array.isArray(resp?.rows) ? resp.rows[0] : null) || resp;

      setAddProvOpen(false);
      setNewProv({ first_name: "", last_name: "", npi: "" });

      const provFresh = await authGet(`/api/providers?org_id=${encodeURIComponent(orgId)}&limit=2000`);
      const provRows = provFresh?.providers || pickRows(provFresh);
      setProviders(provRows);

      const pickedId =
        created?.id ||
        provRows.find((p) => {
          const fn = (p.first_name || "").trim().toLowerCase();
          const ln = (p.last_name || "").trim().toLowerCase();
          return fn === (payload.first_name || "").trim().toLowerCase() && ln === (payload.last_name || "").trim().toLowerCase();
        })?.id;

      if (pickedId) setForm((s) => ({ ...s, provider_id: pickedId }));
    } catch (e) {
      setErr(e.message || "Failed to create provider.");
    } finally {
      setBusy(false);
    }
  };

  const createFacility = async () => {
    if (!orgId) return;
    setBusy(true);
    setErr("");
    try {
      const payload = {
        org_id: orgId,
        name: (newFac.name || "").trim(),
        address1: (newFac.address1 || "").trim() || null,
        city: (newFac.city || "").trim() || null,
        state: (newFac.state || "").trim() || null,
        zip: (newFac.zip || "").trim() || null,
        phone: (newFac.phone || "").trim() || null,

        // ✅ NEW
        clia: (newFac.clia || "").trim() || null,
        ld: (newFac.ld || "").trim() || null,
      };

      const resp = await authPost("/api/facilities", payload);
      const created = resp?.facility || resp?.row || (Array.isArray(resp?.rows) ? resp.rows[0] : null) || resp;

      setAddFacOpen(false);
      setNewFac({ name: "", address1: "", city: "", state: "", zip: "", phone: "", clia: "", ld: "" });

      const facFresh = await authGet(`/api/facilities?org_id=${encodeURIComponent(orgId)}&limit=2000`);
      const facRows = facFresh?.facilities || pickRows(facFresh);
      setFacilities(facRows);

      const pickedId =
        created?.id ||
        facRows.find((f) => (f.name || "").trim().toLowerCase() === (payload.name || "").trim().toLowerCase())?.id;

      if (pickedId) setForm((s) => ({ ...s, facility_id: pickedId }));
    } catch (e) {
      setErr(e.message || "Failed to create facility.");
    } finally {
      setBusy(false);
    }
  };

  const createPatient = async () => {
    if (!orgId) return;
    setBusy(true);
    setErr("");
    try {
      const payload = {
        org_id: orgId,
        first_name: newPat.first_name?.trim() || null,
        last_name: newPat.last_name?.trim() || null,
        dob: newPat.dob || null,
        gender: newPat.gender || null,
        patient_external_id: newPat.patient_external_id?.trim() || null,
      };

      const resp = await authPost("/api/patients", payload);
      const created = resp?.patient || resp?.row || (Array.isArray(resp?.rows) ? resp.rows[0] : null) || resp;

      setAddPatOpen(false);
      setNewPat({ first_name: "", last_name: "", dob: "", gender: "", patient_external_id: "" });

      const patFresh = await authGet(`/api/patients?org_id=${encodeURIComponent(orgId)}&limit=2000`);
      const patRows = patFresh?.patients || pickRows(patFresh);
      setPatients(patRows);

      const pickedId =
        created?.id ||
        patRows.find((p) => {
          const fn = (p.first_name || "").trim().toLowerCase();
          const ln = (p.last_name || "").trim().toLowerCase();
          return fn === (payload.first_name || "").trim().toLowerCase() && ln === (payload.last_name || "").trim().toLowerCase();
        })?.id;

      if (pickedId) {
        const p = patRows.find((x) => x.id === pickedId);
        setForm((s) => ({
          ...s,
          patient_id: pickedId,
          patient_external_id: (p?.patient_external_id ?? p?.external_id ?? s.patient_external_id ?? "") || "",
          patient_dob: toDateInput(p?.dob ?? p?.date_of_birth ?? payload.dob ?? s.patient_dob),
          patient_gender: (p?.gender ?? payload.gender ?? s.patient_gender ?? "") || "",
        }));
      }
    } catch (e) {
      setErr(e.message || "Failed to create patient.");
    } finally {
      setBusy(false);
    }
  };

  const cutMap = useMemo(() => {
    const rows = bundle?.cutoffs || [];
    const m = new Map();
    for (const c of rows) m.set(String(c.analyte || "").toLowerCase(), c);
    return m;
  }, [bundle]);

  const results = useMemo(() => {
    const rows = bundle?.results || [];
    return rows.map((r, idx) => {
      const analyte = r.analyte ?? r.Analyte ?? r.name ?? "";
      const analyteKey = String(analyte || "").toLowerCase();
      const cut = cutMap.get(analyteKey);

      const rawValue = r.result_value ?? r.value ?? r.result ?? r.calculated_concentration ?? null;

      const valNum = Number(rawValue);
      const cutNum = cut?.cut_off != null ? Number(cut.cut_off) : null;

      let flag = "—";
      if (Number.isFinite(valNum) && Number.isFinite(cutNum)) {
        flag = valNum >= cutNum ? "POS" : "NEG";
      }

      const rowId = r.id ?? `${panelId || "panel"}:${String(analyte || idx)}`;

      return {
        ...r,
        id: rowId,
        analyte,
        result_value: rawValue,
        cutoff: cut?.cut_off ?? r.cutoff ?? "—",
        uloq: cut?.uloq ?? r.uloq ?? "—",
        flag: r.flag ?? flag,
      };
    });
  }, [bundle, cutMap, panelId]);

  const titleLine = useMemo(() => {
    const p = bundle?.panel || {};
    return `${p.accession_number || "—"} • ${p.sample_name || "—"}`;
  }, [bundle]);

  const selectedProvider = form.provider_id ? providerById.get(form.provider_id) : null;
  const selectedFacility = form.facility_id ? facilityById.get(form.facility_id) : null;

  const panel = bundle?.panel || {};
  const accessionedAt = panel?.created_at ? new Date(panel.created_at).toLocaleString() : "—";

  const linkedPatient = form.patient_id ? patientById.get(form.patient_id) : null;

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ bgcolor: "white" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6">TOX Panel Hub</Typography>
              <Typography variant="body2" color="text.secondary">
                {titleLine}
              </Typography>
            </Box>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "white" }}>
          {err ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          ) : null}

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
                <Typography variant="subtitle1">Report Header</Typography>
                <Typography variant="body2" color="text.secondary">
                  Accessioned At: {accessionedAt}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Patient"
                    fullWidth
                    size="small"
                    value={form.patient_id}
                    onChange={(e) => {
                      const nextPatientId = e.target.value;
                      if (nextPatientId === "__add__") return setAddPatOpen(true);

                      const p = nextPatientId ? patientById.get(nextPatientId) : null;

                      setForm((s) => ({
                        ...s,
                        patient_id: nextPatientId,
                        patient_external_id: (p?.patient_external_id ?? p?.external_id ?? s.patient_external_id ?? "") || "",
                        patient_dob: toDateInput(p?.dob ?? p?.date_of_birth ?? s.patient_dob),
                        patient_gender: (p?.gender ?? s.patient_gender ?? "") || "",
                      }));
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="__add__">+ Add new patient…</MenuItem>
                    {patients.map((x) => (
                      <MenuItem key={x.id} value={x.id}>
                        {patientLabel(x)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Provider"
                    fullWidth
                    size="small"
                    value={form.provider_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__add__") return setAddProvOpen(true);
                      setForm((s) => ({ ...s, provider_id: v }));
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="__add__">+ Add new provider…</MenuItem>
                    {providers.map((x) => (
                      <MenuItem key={x.id} value={x.id}>
                        {x.full_name || [x.first_name, x.last_name].filter(Boolean).join(" ") || x.name || "—"}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Typography variant="caption" color="text.secondary">
                    NPI: {selectedProvider?.npi || ""}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Facility"
                    fullWidth
                    size="small"
                    value={form.facility_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__add__") return setAddFacOpen(true);
                      setForm((s) => ({ ...s, facility_id: v }));
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="__add__">+ Add new facility…</MenuItem>
                    {facilities.map((x) => (
                      <MenuItem key={x.id} value={x.id}>
                        {x.name || "—"}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Typography variant="caption" color="text.secondary">
                    {selectedFacility
                      ? [
                          selectedFacility.address1,
                          [selectedFacility.city, selectedFacility.state, selectedFacility.zip].filter(Boolean).join(" "),
                        ]
                          .filter(Boolean)
                          .join(" • ")
                      : ""}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Patient ID"
                    fullWidth
                    size="small"
                    value={form.patient_external_id}
                    onChange={(e) => setForm((s) => ({ ...s, patient_external_id: e.target.value }))}
                    helperText={
                      form.patient_id
                        ? "If left blank, server can auto-generate on save."
                        : "Link a patient to save this patient ID server-side."
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="DOB"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.patient_dob}
                    onChange={(e) => setForm((s) => ({ ...s, patient_dob: e.target.value }))}
                    helperText={form.patient_dob ? `Age: ${calcAge(form.patient_dob)}` : " "}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Gender"
                    fullWidth
                    size="small"
                    value={form.patient_gender}
                    onChange={(e) => setForm((s) => ({ ...s, patient_gender: e.target.value }))}
                  >
                    <MenuItem value=""> </MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Specimen Source"
                    fullWidth
                    size="small"
                    value={form.specimen_source}
                    onChange={(e) => setForm((s) => ({ ...s, specimen_source: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Sample Type"
                    fullWidth
                    size="small"
                    value={form.sample_type}
                    onChange={(e) => setForm((s) => ({ ...s, sample_type: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Requisition Date"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.requisition_at}
                    onChange={(e) => setForm((s) => ({ ...s, requisition_at: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Collected At"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.collected_at}
                    onChange={(e) => setForm((s) => ({ ...s, collected_at: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Received At"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.received_at}
                    onChange={(e) => setForm((s) => ({ ...s, received_at: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Report Date"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.report_date}
                    onChange={(e) => setForm((s) => ({ ...s, report_date: e.target.value }))}
                    helperText="Defaults to the CSV import timestamp."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Medications"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={form.medications}
                    onChange={(e) => setForm((s) => ({ ...s, medications: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Linked Patient: {linkedPatient ? patientLabel(linkedPatient) || linkedPatient.id : "None"}
                  </Typography>
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
                <Button onClick={load} disabled={busy}>
                  Reload
                </Button>
                <Button variant="contained" onClick={saveHeader} disabled={busy}>
                  {busy ? "Saving…" : "Save Header"}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Results
              </Typography>

              <Box sx={{ height: 360, width: "100%" }}>
                <DataGrid
                  rows={results || []}
                  getRowId={(r) => r.id || r.result_id || r.__rowid}
                  columns={[
                    { field: "analyte", headerName: "Analyte", flex: 1, minWidth: 220 },
                    { field: "result_value", headerName: "Result", width: 140 },
                    { field: "units", headerName: "Units", width: 120 },
                    { field: "cutoff", headerName: "Cutoff", width: 140 },
                    { field: "flag", headerName: "Flag", width: 110 },
                    { field: "uloq", headerName: "ULOQ", width: 140 },
                  ]}
                  disableRowSelectionOnClick
                  getRowHeight={() => 44}
                  loading={busy && !bundle}
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                Flags are computed from org cutoffs (POS if result ≥ cutoff).
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Patient History
              </Typography>
              {(bundle?.history || []).length ? (
                <Box sx={{ display: "grid", gap: 1 }}>
                  {bundle.history.map((h) => (
                    <Box
                      key={h.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography variant="body2">{h.accession_number || h.id}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(h.received_at ? new Date(h.received_at) : h.created_at ? new Date(h.created_at) : null)?.toLocaleString?.() || "—"}
                        {" • "}
                        {String(h.status || "").toUpperCase()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No history found (patient may not be linked yet).
                </Typography>
              )}
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions sx={{ bgcolor: "white" }}>
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Provider */}
      <Dialog open={addProvOpen} onClose={busy ? undefined : () => setAddProvOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Provider</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                fullWidth
                size="small"
                value={newProv.first_name}
                onChange={(e) => setNewProv((s) => ({ ...s, first_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                fullWidth
                size="small"
                value={newProv.last_name}
                onChange={(e) => setNewProv((s) => ({ ...s, last_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="NPI"
                fullWidth
                size="small"
                value={newProv.npi}
                onChange={(e) => setNewProv((s) => ({ ...s, npi: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProvOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createProvider} disabled={busy}>
            {busy ? "Saving…" : "Create Provider"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Facility */}
      <Dialog open={addFacOpen} onClose={busy ? undefined : () => setAddFacOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Facility</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Facility Name"
                fullWidth
                size="small"
                value={newFac.name}
                onChange={(e) => setNewFac((s) => ({ ...s, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                size="small"
                value={newFac.address1}
                onChange={(e) => setNewFac((s) => ({ ...s, address1: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="City"
                fullWidth
                size="small"
                value={newFac.city}
                onChange={(e) => setNewFac((s) => ({ ...s, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="State"
                fullWidth
                size="small"
                value={newFac.state}
                onChange={(e) => setNewFac((s) => ({ ...s, state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="ZIP"
                fullWidth
                size="small"
                value={newFac.zip}
                onChange={(e) => setNewFac((s) => ({ ...s, zip: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone"
                fullWidth
                size="small"
                value={newFac.phone}
                onChange={(e) => setNewFac((s) => ({ ...s, phone: e.target.value }))}
              />
            </Grid>

            {/* ✅ NEW CLIA + LD */}
            <Grid item xs={12} md={6}>
              <TextField
                label="CLIA"
                fullWidth
                size="small"
                value={newFac.clia}
                onChange={(e) => setNewFac((s) => ({ ...s, clia: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="LD"
                fullWidth
                size="small"
                value={newFac.ld}
                onChange={(e) => setNewFac((s) => ({ ...s, ld: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFacOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createFacility} disabled={busy}>
            {busy ? "Saving…" : "Create Facility"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Patient */}
      <Dialog open={addPatOpen} onClose={busy ? undefined : () => setAddPatOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Patient</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                fullWidth
                size="small"
                value={newPat.first_name}
                onChange={(e) => setNewPat((s) => ({ ...s, first_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                fullWidth
                size="small"
                value={newPat.last_name}
                onChange={(e) => setNewPat((s) => ({ ...s, last_name: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="DOB"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={newPat.dob}
                onChange={(e) => setNewPat((s) => ({ ...s, dob: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Gender"
                fullWidth
                size="small"
                value={newPat.gender}
                onChange={(e) => setNewPat((s) => ({ ...s, gender: e.target.value }))}
              >
                <MenuItem value=""> </MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Patient ID (External)"
                fullWidth
                size="small"
                value={newPat.patient_external_id}
                onChange={(e) => setNewPat((s) => ({ ...s, patient_external_id: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPatOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createPatient} disabled={busy}>
            {busy ? "Saving…" : "Create Patient"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function LabResults() {
  const theme = useTheme();
  const { search } = useLocation();
  const navigate = useNavigate();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const [toast, setToast] = useState({ open: false, msg: "", severity: "error" });

  // ✅ NO MORE HARDCODED ORGS
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  const [orgId, setOrgId] = useState(qs.get("org") || "");
  const [testType, setTestType] = useState(qs.get("type") || "TOX");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [importOpen, setImportOpen] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPanelId, setEditorPanelId] = useState(null);

  // ✅ Bulk selection + actions
    const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAnchor, setBulkAnchor] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // ✅ Pagination (client-side)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const { page, pageSize } = paginationModel;

  // If filters/search change, go back to page 1
  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [searchText, facilityFilter, providerFilter, sourceFilter, statusFilter, orgId, testType]);

  // Slice the filtered rows for the current page

  const syncToUrl = (nextOrg, nextType) => {
    const url = new URL(window.location.href);
    if (nextOrg) url.searchParams.set("org", nextOrg);
    if (nextType) url.searchParams.set("type", nextType);
    navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
  };

  // ✅ Load orgs from backend
  useEffect(() => {
    let cancelled = false;

    async function loadOrgs() {
      setOrgsLoading(true);
      try {
        const j = await authGet("/api/organizations");
        const list = j?.organizations || j?.rows || pickRows(j) || [];

        if (cancelled) return;

        setOrgs(list);

        // Choose org:
        // 1) URL ?org=
        // 2) first org returned
        // 3) leave blank
        if (!orgId) {
          const urlOrg = qs.get("org");
          const first = list?.[0]?.id;
          setOrgId(urlOrg || first || "");
        }
      } catch (e) {
        console.error("Failed to load organizations:", e);
        if (!cancelled) setOrgs([]);
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    }

    loadOrgs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPanelId = (row) => row?.panel_id || row?.raw?.panel_id || row?.raw?.tox_panel_id || row?.id;

  const fetchRows = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const j = await authGet(
        `/api/lab-results?org_id=${encodeURIComponent(orgId)}&type=${encodeURIComponent(testType)}&limit=5000`
      );
      const rawRows = j?.rows || pickRows(j);

      const mapped = rawRows.map((r, idx) => {
        const realId = r.id || r.panel_id || r.tox_panel_id;
        return {
          id: realId || `${r.accession_number || "row"}:${idx}`,
          panel_id: realId || null,
          accession_number: r.accession_number || "—",
          received: r.received_at || r.created_at || null,
          type: r.type || r.test_type || testType,
          patient: r.patient || "—",
          provider: r.provider || "—",
          facility: r.facility || "—",
          source: r.source || "—",
          status: r.status || "pending",
          report_last_generated_at: r.report_last_generated_at || null,
          raw: r,
        };
      });

      setRows(mapped);
    } catch (e) {
      setRows([]);
      setToast({ open: true, msg: `Failed to load results: ${e.message}`, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;
    syncToUrl(orgId, testType);
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, testType]);

  const facilities = useMemo(() => ["all", ...Array.from(new Set(rows.map((r) => r.facility).filter(Boolean))).sort()], [rows]);
  const providers = useMemo(() => ["all", ...Array.from(new Set(rows.map((r) => r.provider).filter(Boolean))).sort()], [rows]);
  const sources = useMemo(() => ["all", ...Array.from(new Set(rows.map((r) => r.source).filter(Boolean))).sort()], [rows]);
  const statuses = useMemo(() => {
    const set = new Set(rows.map((r) => String(r.status || "").toLowerCase()).filter(Boolean));
    set.add("deleted");
    return ["all", ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rows.filter((r) => {
      const st = String(r.status || "").toLowerCase();
      const want = String(statusFilter || "").toLowerCase();

      // ✅ hide deleted unless explicitly filtering for it
      if (want === "all") {
        if (st === "deleted") return false;
      } else {
        if (st !== want) return false;
      }

      if (facilityFilter !== "all" && r.facility !== facilityFilter) return false;
      if (providerFilter !== "all" && r.provider !== providerFilter) return false;
      if (sourceFilter !== "all" && r.source !== sourceFilter) return false;

      if (!q) return true;

      return `${r.accession_number} ${r.patient} ${r.provider} ${r.facility} ${r.source} ${r.status} ${r.type}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, searchText, facilityFilter, providerFilter, sourceFilter, statusFilter]);

  const selectedRows = useMemo(() => {
    const idSet = new Set(selectedIds);
    return filtered.filter((r) => idSet.has(r.id));
  }, [filtered, selectedIds]);

  const openEditor = (row) => {
    setEditorPanelId(getPanelId(row));
    setEditorOpen(true);
  };

  const onMenuOpen = (e, row) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const onMenuClose = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const copyAccession = async (row) => {
    try {
      await navigator.clipboard.writeText(String(row?.accession_number || ""));
      setToast({ open: true, msg: "Copied accession to clipboard.", severity: "success" });
    } catch {
      setToast({ open: true, msg: "Copy failed.", severity: "error" });
    }
  };

  const deletePanel = async (row) => {
    try {
      if (!orgId) throw new Error("Missing orgId");
      const pid = getPanelId(row);
      if (!pid) throw new Error("Missing panel id");

      const ok = window.confirm(`Delete panel ${row.accession_number || pid}?\n\nThis will hide it from the UI.`);
      if (!ok) return;

      await authPost(`/api/tox/panels/${encodeURIComponent(pid)}/delete`, { org_id: orgId });

      setToast({ open: true, msg: "Panel deleted (soft).", severity: "success" });
      fetchRows();
    } catch (e) {
      setToast({ open: true, msg: `Delete failed: ${e.message}`, severity: "error" });
    }
  };

  const generateReport = async (_e, row) => {
    let win = null;
    try {
      // Open synchronously from the click handler and keep the window handle.
      // Using noopener here causes some browsers to return null; then the later
      // blob window.open happens after await and gets popup-blocked while the UI
      // still says "Report generated."
      win = window.open("about:blank", "_blank", "width=1000,height=800");
      if (win) {
        win.document.write("<html><body style='font-family:sans-serif;padding:24px'>Preparing report PDF…</body></html>");
        win.document.close();
      }
    } catch {}

    try {
      if (!orgId) throw new Error("Missing orgId");
      const pid = getPanelId(row);
      if (!pid) throw new Error("Missing panel id");

      const blob = await authFetchBlob(`/api/tox/panels/${encodeURIComponent(pid)}/generate-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_id: orgId }),
      });

      const url = URL.createObjectURL(blob);

      if (win && !win.closed) {
        win.location.href = url;
      } else {
        const fallback = window.open(url, "_blank");
        if (!fallback) throw new Error("Report PDF was created, but the browser blocked the popup. Allow popups for this site and try again.");
      }

      setToast({ open: true, msg: "Report generated.", severity: "success" });
      fetchRows();

      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      if (win && !win.closed) {
        try {
          win.document.body.innerHTML = "";
          const pre = win.document.createElement("pre");
          pre.style.fontFamily = "sans-serif";
          pre.style.whiteSpace = "pre-wrap";
          pre.style.padding = "24px";
          pre.style.color = "#b00020";
          pre.textContent = `Generate failed: ${String(err.message || err)}`;
          win.document.body.appendChild(pre);
        } catch {
          win.close();
        }
      }
      setToast({ open: true, msg: `Generate failed: ${err.message}`, severity: "error" });
    }
  };

  const bulkGenerateReports = async () => {
    const reportWindows = [];
    try {
      if (!orgId) throw new Error("Missing orgId");
      if (!selectedRows.length) return;

      // Open placeholders before awaiting network calls so browser popup blockers
      // treat them as user-initiated windows.
      selectedRows.forEach((r, idx) => {
        try {
          const w = window.open("about:blank", "_blank", "width=1000,height=800");
          if (w) {
            w.document.write(`<html><body style='font-family:sans-serif;padding:24px'>Preparing report ${idx + 1} of ${selectedRows.length}…</body></html>`);
            w.document.close();
          }
          reportWindows[idx] = w;
        } catch {
          reportWindows[idx] = null;
        }
      });

      setBulkBusy(true);

      for (let i = 0; i < selectedRows.length; i += 1) {
        const r = selectedRows[i];
        const pid = getPanelId(r);
        if (!pid) continue;

        const blob = await authFetchBlob(`/api/tox/panels/${encodeURIComponent(pid)}/generate-report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: orgId }),
        });

        const url = URL.createObjectURL(blob);
        const w = reportWindows[i];
        if (w && !w.closed) w.location.href = url;
        else {
          const fallback = window.open(url, "_blank");
          if (!fallback) throw new Error("One or more report PDFs were created, but the browser blocked the popup. Allow popups for this site and try again.");
        }
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      }

      setToast({ open: true, msg: `Generated ${selectedRows.length} report(s).`, severity: "success" });
      fetchRows();
    } catch (e) {
      reportWindows.forEach((w) => {
        if (w && !w.closed) {
          try {
            w.document.body.innerHTML = "";
            const pre = w.document.createElement("pre");
            pre.style.fontFamily = "sans-serif";
            pre.style.whiteSpace = "pre-wrap";
            pre.style.padding = "24px";
            pre.style.color = "#b00020";
            pre.textContent = `Bulk generate failed: ${String(e.message || e)}`;
            w.document.body.appendChild(pre);
          } catch {}
        }
      });
      setToast({ open: true, msg: `Bulk generate failed: ${e.message}`, severity: "error" });
    } finally {
      setBulkBusy(false);
      setBulkAnchor(null);
    }
  };

  const bulkDeletePanels = async () => {
    try {
      if (!orgId) throw new Error("Missing orgId");
      if (!selectedRows.length) return;

      const ok = window.confirm(`Delete ${selectedRows.length} panel(s)?\n\nThis will soft-delete them (hide from UI).`);
      if (!ok) return;

      setBulkBusy(true);

      for (const r of selectedRows) {
        const pid = getPanelId(r);
        if (!pid) continue;
        await authPost(`/api/tox/panels/${encodeURIComponent(pid)}/delete`, { org_id: orgId });
      }

      setToast({ open: true, msg: `Deleted ${selectedRows.length} panel(s).`, severity: "success" });
      setSelectedIds([]);
      fetchRows();
    } catch (e) {
      setToast({ open: true, msg: `Bulk delete failed: ${e.message}`, severity: "error" });
    } finally {
      setBulkBusy(false);
      setBulkAnchor(null);
    }
  };

  const columns = [
    {
      field: "menu",
      headerName: "",
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Tooltip title="Row menu">
            <IconButton size="small" onClick={(e) => onMenuOpen(e, params.row)} sx={{ zIndex: 2 }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: "accession_number", headerName: "Accession / Result", width: 160 },
    { field: "received", headerName: "Received", width: 150, renderCell: ({ row }) => dt(row.received) },
    { field: "type", headerName: "Type", width: 90 },
    { field: "patient", headerName: "Patient", flex: 1, minWidth: 170 },
    { field: "provider", headerName: "Provider", flex: 1, minWidth: 170 },
    { field: "facility", headerName: "Facility", flex: 1, minWidth: 170 },
    { field: "source", headerName: "Source", flex: 1, minWidth: 160 },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ row }) => (
        <Chip size="small" label={String(row.status || "").toUpperCase()} color={colorForStatus(row.status)} variant="outlined" />
      ),
    },
    {
      field: "report",
      headerName: "Report",
      width: 190,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
            <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => generateReport(null, params.row)}>
            Create
          </Button>
          <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => generateReport(null, params.row)}>
            View
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        flex: "1 1 auto",
        minHeight: 0,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: { xs: 1, md: 1.5 },
        gap: 1.25,
      }}
    >
      <Box sx={{ flex: "0 0 auto", minWidth: 0 }}>
        <Header title="Lab Results" subtitle="Review results, import TOX panels, and generate reports" />
      </Box>

      <Box
        display="flex"
        gap={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ flex: "0 0 auto", minWidth: 0, "& .MuiButton-root": { whiteSpace: "nowrap" } }}
      >
          <TextField
            select
            size="small"
            label="Organization"
            value={orgId || ""}
            onChange={(e) => setOrgId(e.target.value)}
            sx={{ minWidth: 220 }}
            disabled={orgsLoading || !orgs.length}
          >
            {orgs.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            variant="outlined"
            disabled={!orgId}
            onClick={() => navigate(`/admin/organizations?org=${encodeURIComponent(orgId)}`)}
          >
            Report Settings
          </Button>

          <TextField select size="small" label="Test Type" value={testType} onChange={(e) => setTestType(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="TOX">TOX</MenuItem>
          </TextField>

          <TextField
            size="small"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />

          <Button
            variant="outlined"
            disabled={!selectedIds.length || bulkBusy}
            onClick={(e) => setBulkAnchor(e.currentTarget)}
            startIcon={bulkBusy ? <CircularProgress size={16} /> : <MoreVertIcon />}
          >
            Bulk Actions {selectedIds.length ? `(${selectedIds.length})` : ""}
          </Button>

          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows} disabled={!orgId}>
            Refresh
          </Button>

          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)} disabled={!orgId}>
            Import {testType} CSV
          </Button>
        </Box>

      <Card sx={{ flex: "0 0 auto" }}>
        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <TextField select label="Facility" fullWidth size="small" value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)}>
                {facilities.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f === "all" ? "All" : f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField select label="Provider" fullWidth size="small" value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
                {providers.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p === "all" ? "All" : p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField select label="Source" fullWidth size="small" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                {sources.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s === "all" ? "All" : s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField select label="Status" fullWidth size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s === "all" ? "All" : String(s).toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ flex: "1 1 auto", minHeight: 0, minWidth: 0, display: "flex", overflow: "hidden" }}>
        <CardContent sx={{ flex: 1, minHeight: 0, minWidth: 0, p: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, minHeight: 0, width: "100%", minWidth: 0, overflow: "hidden" }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              checkboxSelection
              rowSelectionModel={selectedIds}
              onRowSelectionModelChange={(model) => setSelectedIds(Array.isArray(model) ? model : [])}
              disableRowSelectionOnClick
              getRowHeight={() => 52}
              sx={{
                "& .MuiDataGrid-columnHeaders": { background: theme.palette.background.paper },
                "& .MuiDataGrid-main": { overflow: "hidden" },
                "& .MuiDataGrid-virtualScroller": { overflow: "auto" },
              }}

              pagination
              paginationMode="client"
              pageSizeOptions={[25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                setPaginationModel((prev) => ({
                  page: model.page ?? 0,
                  // if pageSize changes, reset to page 0
                  pageSize: model.pageSize ?? prev.pageSize,
                }));
              }}

              onRowDoubleClick={(params, e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                openEditor(params.row);
              }}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">
              {filtered.length === 0
                ? "Showing 0 results"
                : `Showing ${page * pageSize + 1}–${Math.min((page + 1) * pageSize, filtered.length)} of ${filtered.length} results`}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <ImportToxCsvDialog open={importOpen} onClose={() => setImportOpen(false)} orgId={orgId} testType={testType} onImported={() => fetchRows()} />

      <Menu anchorEl={bulkAnchor} open={Boolean(bulkAnchor)} onClose={() => setBulkAnchor(null)}>
        <MenuItem onClick={() => bulkGenerateReports()} disabled={!selectedIds.length || bulkBusy}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate Reports</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => bulkDeletePanels()} disabled={!selectedIds.length || bulkBusy}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Panels</ListItemText>
        </MenuItem>
      </Menu>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={onMenuClose}>
        <MenuItem
          onClick={() => {
            const r = menuRow;
            onMenuClose();
            openEditor(r);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit / View Details</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const r = menuRow;
            onMenuClose();
            generateReport(null, r);
          }}
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Report</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            const r = menuRow;
            onMenuClose();
            deletePanel(r);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Panel</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const r = menuRow;
            onMenuClose();
            copyAccession(r);
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Accession</ListItemText>
        </MenuItem>
      </Menu>

      <PanelHubDialog open={editorOpen} onClose={() => setEditorOpen(false)} orgId={orgId} panelId={editorPanelId} onSaved={() => fetchRows()} />

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity || "info"} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}